import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Star, Plus, Copy, CheckCheck, Trash2, Link as LinkIcon, Clock, CheckCircle, Eye } from 'lucide-react';
import { webvonkClient } from '../lib/supabase';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ACCENT = '#A020C6';
const SITE = 'https://whenconnect.nl';

const Wrap = styled.div`
  height: 100%;
  background: ${p => p.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  padding: 40px 20px 60px;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeUp} 0.3s ease both;
  @media (max-width: 600px) { padding: 24px 14px 40px; }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const TitleRow = styled.div`display: flex; align-items: center; gap: 12px;`;

const TitleIcon = styled.div`
  width: 36px; height: 36px;
  border-radius: 10px;
  background: ${ACCENT}18;
  display: flex; align-items: center; justify-content: center;
  color: ${ACCENT}; flex-shrink: 0;
`;

const Title = styled.h1`
  font-size: 22px; font-weight: 700;
  color: ${p => p.theme.colors.text}; margin: 0;
`;

const CreateBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 9px 18px;
  background: ${ACCENT};
  color: #fff;
  border: none; border-radius: 10px;
  font-size: 13px; font-weight: 600;
  font-family: inherit; cursor: pointer;
  transition: filter 0.15s;
  &:hover { filter: brightness(0.9); }
`;

const Modal = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 18px;
  padding: 28px 24px 24px;
  width: 100%; max-width: 420px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.3);
`;

const ModalTitle = styled.h2`
  font-size: 18px; font-weight: 700;
  color: ${p => p.theme.colors.text}; margin: 0 0 6px;
`;

const ModalSub = styled.p`
  font-size: 13px;
  color: ${p => p.theme.colors.textMuted}; margin: 0 0 20px;
`;

const Input = styled.input`
  width: 100%; box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px; font-family: inherit;
  outline: none;
  margin-bottom: 16px;
  &:focus { border-color: ${ACCENT}; box-shadow: 0 0 0 3px ${ACCENT}18; }
  &::placeholder { color: ${p => p.theme.colors.textMuted}; opacity: 0.6; }
`;

const ModalBtns = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;

const Btn = styled.button`
  padding: 9px 20px;
  border-radius: 999px; border: none;
  font-size: 13px; font-weight: 600; font-family: inherit;
  cursor: pointer; transition: filter 0.15s;
  background: ${p => p.$primary ? ACCENT : p.theme.colors.border};
  color: ${p => p.$primary ? '#fff' : p.theme.colors.text};
  &:hover { filter: brightness(0.92); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const LinkCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  padding: 16px 20px;
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 10px;
  transition: border-color 0.15s;
  &:hover { border-color: ${ACCENT}40; }
`;

const StatusDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$used ? '#22c55e' : ACCENT};
`;

const CardInfo = styled.div`flex: 1; min-width: 0;`;

const CardLabel = styled.div`
  font-size: 14px; font-weight: 600;
  color: ${p => p.theme.colors.text}; margin-bottom: 3px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const CardUrl = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textMuted};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.textMuted};
  display: flex; align-items: center; gap: 5px;
  white-space: nowrap; flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 32px; height: 32px;
  border-radius: 8px; border: none;
  background: transparent;
  color: ${p => p.theme.colors.textMuted};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0;
  transition: background 0.12s, color 0.12s;
  &:hover { background: ${p => p.$danger ? 'rgba(239,68,68,0.1)' : p.theme.colors.border}; color: ${p => p.$danger ? '#ef4444' : p.theme.colors.text}; }
`;

const Empty = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 80px 20px;
  color: ${p => p.theme.colors.textMuted};
  font-size: 15px; text-align: center; gap: 12px;
  svg { opacity: 0.3; }
`;

const SectionLabel = styled.div`
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: ${p => p.theme.colors.textMuted};
  margin-bottom: 10px; margin-top: 28px;
`;

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'zojuist';
  if (m < 60) return `${m}m geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}u geleden`;
  return `${Math.floor(h / 24)}d geleden`;
}

export default function WhenconnectErvaringen() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState('30');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await webvonkClient
      .from('wc_review_links')
      .select('*')
      .order('aangemaakt_op', { ascending: false });
    setLinks(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    const verloopt_op = new Date(Date.now() + parseInt(expiry) * 24 * 60 * 60 * 1000).toISOString();
    await webvonkClient.from('wc_review_links').insert({ label: label.trim() || null, verloopt_op });
    setCreating(false);
    setShowCreate(false);
    setLabel('');
    setExpiry('30');
    load();
  };

  const copyUrl = (token) => {
    const url = `${SITE}/recensie/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const openReview = async (linkId) => {
    const { data } = await webvonkClient
      .from('wc_recensies')
      .select('*')
      .eq('link_id', linkId)
      .single();
    setReviewModal(data || null);
  };

  const handleDelete = async () => {
    await webvonkClient.from('wc_review_links').delete().eq('id', deleteId);
    setDeleteId(null);
    load();
  };

  const open = links.filter(l => !l.gebruikt);
  const used = links.filter(l => l.gebruikt);

  return (
    <Wrap>
      <Content>
        <Header>
          <TitleRow>
            <TitleIcon><Star size={18} /></TitleIcon>
            <Title>Ervaringen</Title>
          </TitleRow>
          <CreateBtn onClick={() => setShowCreate(true)}>
            <Plus size={15} /> Nieuwe link
          </CreateBtn>
        </Header>

        {loading && <div style={{ color: 'grey', fontSize: 13 }}>Laden...</div>}

        {!loading && links.length === 0 && (
          <Empty>
            <LinkIcon size={40} />
            <span>Nog geen links aangemaakt.</span>
          </Empty>
        )}

        {!loading && open.length > 0 && (
          <>
            <SectionLabel>Openstaand</SectionLabel>
            {open.map(l => (
              <LinkCard key={l.id}>
                <StatusDot $used={false} />
                <CardInfo>
                  <CardLabel>{l.label || 'Naamloos'}</CardLabel>
                  <CardUrl>{SITE}/recensie/{l.token}</CardUrl>
                </CardInfo>
                <CardMeta><Clock size={12} /> {timeAgo(l.aangemaakt_op)}</CardMeta>
                <IconBtn onClick={() => copyUrl(l.token)} title="Kopieer link">
                  {copied === l.token ? <CheckCheck size={15} style={{ color: '#22c55e' }} /> : <Copy size={15} />}
                </IconBtn>
                <IconBtn $danger onClick={() => setDeleteId(l.id)} title="Verwijderen">
                  <Trash2 size={15} />
                </IconBtn>
              </LinkCard>
            ))}
          </>
        )}

        {!loading && used.length > 0 && (
          <>
            <SectionLabel>Gebruikt</SectionLabel>
            {used.map(l => (
              <LinkCard key={l.id} style={{ opacity: 0.75 }}>
                <StatusDot $used={true} />
                <CardInfo>
                  <CardLabel>{l.label || 'Naamloos'}</CardLabel>
                  <CardUrl>{SITE}/recensie/{l.token}</CardUrl>
                </CardInfo>
                <CardMeta><CheckCircle size={12} style={{ color: '#22c55e' }} /> ingevuld</CardMeta>
                <IconBtn onClick={() => openReview(l.id)} title="Bekijk recensie">
                  <Eye size={15} />
                </IconBtn>
              </LinkCard>
            ))}
          </>
        )}
      </Content>

      {/* Create modal */}
      {showCreate && (
        <Modal onClick={() => setShowCreate(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Nieuwe recensielink</ModalTitle>
            <ModalSub>Geef de link een naam zodat je weet voor wie hij is.</ModalSub>
            <Input
              placeholder="bijv. klant januari 2025"
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              autoFocus
            />
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'inherit', opacity: 0.4, marginBottom: 8 }}>Verloopt na</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[['7', '1 week'], ['30', '1 maand'], ['90', '3 maanden'], ['180', '6 maanden'], ['365', '1 jaar']].map(([val, lbl]) => (
                  <button
                    key={val}
                    onClick={() => setExpiry(val)}
                    style={{
                      padding: '6px 14px', borderRadius: 999, border: `1.5px solid ${expiry === val ? ACCENT : 'rgba(128,128,128,0.2)'}`,
                      background: expiry === val ? ACCENT : 'transparent',
                      color: expiry === val ? '#fff' : 'inherit',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.15s',
                    }}
                  >{lbl}</button>
                ))}
              </div>
            </div>
            <ModalBtns>
              <Btn onClick={() => { setShowCreate(false); setLabel(''); }}>Annuleren</Btn>
              <Btn $primary onClick={handleCreate} disabled={creating}>
                {creating ? 'Aanmaken...' : 'Aanmaken'}
              </Btn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <Modal onClick={() => setDeleteId(null)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>Link verwijderen</ModalTitle>
            <ModalSub>Dit kan niet ongedaan worden gemaakt.</ModalSub>
            <ModalBtns>
              <Btn onClick={() => setDeleteId(null)}>Annuleren</Btn>
              <Btn $primary style={{ background: '#ef4444' }} onClick={handleDelete}>Verwijderen</Btn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}

      {reviewModal !== undefined && reviewModal !== null && (
        <Modal onClick={() => setReviewModal(undefined)}>
          <ModalBox onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <ModalTitle>Recensie</ModalTitle>
            <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
              {[1,2,3,4,5].map(i => (
                <svg key={i} width="16" height="16" viewBox="0 0 24 24"
                  fill={i <= (reviewModal.rating || 0) ? ACCENT : 'none'}
                  stroke={i <= (reviewModal.rating || 0) ? ACCENT : '#d1d1d6'}
                  strokeWidth="1.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </div>
            <ModalSub style={{ fontStyle: 'italic', marginBottom: 20 }}>"{reviewModal.tekst}"</ModalSub>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>{reviewModal.naam}</div>
            {(reviewModal.functie || reviewModal.bedrijf) && (
              <div style={{ fontSize: 12, opacity: 0.5, marginTop: 2 }}>
                {[reviewModal.functie, reviewModal.bedrijf].filter(Boolean).join(' · ')}
              </div>
            )}
            <ModalBtns style={{ marginTop: 24 }}>
              <Btn onClick={() => setReviewModal(undefined)}>Sluiten</Btn>
            </ModalBtns>
          </ModalBox>
        </Modal>
      )}
    </Wrap>
  );
}
